import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UnifiedConnectionProvider } from "@common/schemas/organization-unified-connection.schema";
import { unifiedConnectionId } from "@common/helpers/unifiedConnectionId";
import { useScope } from "@state/scopeContext.lib";
import { organizationIntegrationQueryKeys } from "@api/organizationIntegration.api";
import { useOrgConnect } from "contexts/OrgConnect.context";
import { useOverlays } from "contexts/OverlayContext";

/** The connection a provider just created, in provider-native terms. */
interface ConnectedConnection {
    provider: UnifiedConnectionProvider;
    /** The provider's own connection id, int-keyed providers included. */
    connectionId: string;
}

interface ConnectTarget {
    /** Undefined for an organization-level connect, which has no client yet. */
    workspaceId: string | undefined;
    organizationId: string | undefined;
    isOrgConnect: boolean;
    refreshConnections: (connection?: ConnectedConnection) => Promise<void>;
}

/**
 * Where a connect flow is being run from. In a workspace this is the active
 * workspace; in the organization view there is no client yet, so the request
 * carries no workspace and the org integrations table is refreshed afterwards.
 * The workspace post-connect modals are neutralized by `OrgConnectProvider`.
 *
 * An org-level connect leaves its accounts with no client and no entity, so
 * once the table is refreshed the new connection is handed to the post-connect
 * assignment modal. That runs as a global overlay because the connect flow
 * lives in the command palette, which unmounts the moment it closes. Providers
 * that don't pass the connection they created simply skip the modal — the
 * accounts are still in the table, waiting to be assigned there.
 */
export function useConnectTarget(): ConnectTarget {
    const { workspaceId } = useScope();
    const orgConnect = useOrgConnect();
    const queryClient = useQueryClient();
    const { openOverlay } = useOverlays();

    const organizationId = orgConnect?.organizationId;
    const isOrgConnect = organizationId !== undefined;

    const refreshConnections = useCallback(
        async (connection?: ConnectedConnection) => {
            if (!organizationId) {
                return;
            }
            await queryClient.invalidateQueries({
                queryKey: organizationIntegrationQueryKeys.all(organizationId),
            });
            if (connection === undefined) {
                return;
            }
            openOverlay("organizationPostConnectAssignment", {
                organizationId,
                connectionId: unifiedConnectionId(connection),
            });
        },
        [organizationId, queryClient, openOverlay],
    );

    return {
        workspaceId: isOrgConnect ? undefined : (workspaceId ?? undefined),
        organizationId,
        isOrgConnect,
        refreshConnections,
    };
}
